/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/NotificationBell/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./index.module.css";

import { IconButton } from "@mui/material";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";

import { ReactComponent as RequirementsIcon } from "../../assets/icons/SideMenu/RequirementsIcon.svg";
import { ReactComponent as EventsIcon } from "../../assets/icons/SideMenu/EventsIcon.svg";
import { ReactComponent as ChatIcon } from "../../assets/icons/SideMenu/ChatIcon.svg";
import { useNotifications } from "../../context/NotificationContext";
import { refreshToken, signOut } from "../../utils/common.js";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { formatNotificationMessage as buildNotificationMessage } from "../../utils/notifications/formatNotificationMessage.js";

const NotificationBell = ({ navigate }) => {
  const { t } = useTranslation();
  const { notifications, setNotifications, unreadCount } = useNotifications();

  const [notificationMenuOpened, setNotificationMenuOpened] = useState(false);

  const handleNotificationMenuOpen = () => {
    setNotificationMenuOpened(!notificationMenuOpened);
  };

  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setNotificationMenuOpened(false);
      }
    }

    if (notificationMenuOpened) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationMenuOpened]);

  const formatNotificationMessage = (notification) =>
    buildNotificationMessage(notification, t);

  const formatNotificationModule = (type) => {
    switch (type) {
      case "requirement":
        return (
          <>
            <RequirementsIcon className={styles.menuIcon} />
            <span className={styles.moduleText}>
              {t("components.notificationsList.requirements")}
            </span>
          </>
        );
      case "event":
        return (
          <>
            <EventsIcon className={styles.menuIcon} />
            <span className={styles.moduleText}>
              {t("components.notificationsList.events")}
            </span>
          </>
        );
      case "chat":
        return (
          <>
            <ChatIcon className={styles.menuIcon} />
            <span className={styles.moduleText}>
              {t("components.notificationsList.chat")}
            </span>
          </>
        );
      case "appointment":
        return (
          <>
            <EventsIcon className={styles.menuIcon} />
            <span className={styles.moduleText}>
              {t("components.notificationsList.appointment")}
            </span>
          </>
        );
      case "connection":
        return (
          <>
            <ChatIcon className={styles.menuIcon} />
            <span className={styles.moduleText}>
              {t("components.notificationsList.chat")}
            </span>
          </>
        );
      case "b2bEvent":
        return (
          <>
            <EventsIcon className={styles.menuIcon} />
            <span className={styles.moduleText}>
              {t("components.notificationsList.events")}
            </span>
          </>
        );
      case "customNotification":
        return (
          <>
            <IconButton
              style={{
                color: "#133c9b",
                width: "14px",
                height: "14px",
              }}
            >
              <NotificationsNoneRoundedIcon fontSize="small" />
            </IconButton>
            <span className={styles.moduleText}>
              {t("components.notificationsList.notification")}
            </span>
          </>
        );
      default:
        return t("common.notification");
    }
  };

  const formatTimePassed = (date) => {
    let timeNumber;
    const now = dayjs(new Date().toISOString());
    const notificationDate = dayjs(date);
    const diff = now.diff(notificationDate);

    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) {
      return t("components.notificationsList.secondsAgo");
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      timeNumber = minutes;
      return t("components.notificationsList.minutesAgo", {
        timeUnit: timeNumber,
      });
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      timeNumber = hours;
      return t("components.notificationsList.hoursAgo", {
        timeUnit: timeNumber,
      });
    }

    const days = Math.floor(hours / 24);
    if (days < 7) {
      timeNumber = days;
      return t("components.notificationsList.daysAgo", {
        timeUnit: timeNumber,
      });
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      timeNumber = weeks;
      return t("components.notificationsList.weeksAgo", {
        timeUnit: timeNumber,
      });
    } else {
      const months = Math.floor(days / 30);
      timeNumber = months;
      return t("components.notificationsList.monthsAgo", {
        timeUnit: timeNumber,
      });
    }
  };

  const markAllAsRead = async () => {
    const options = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    try {
      const userId = localStorage.getItem("userId");
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}notification/all/read/${userId}`,
        options,
      );
      const data = await response.json();
      logger.info("markAllAsRead data: ", { data: data });
      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        logger.info("newTokens: ", { data: newTokens });
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await markAllAsRead();
      }
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        leido: true,
      }));
      setNotifications(updatedNotifications);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const goToPage = async (notification) => {
    if (!notification.datosMensaje || !notification.datosMensaje.idObjeto) {
      logger.error("Notification or notification data is missing");
      return;
    }

    if (!notification.leido) {
      try {
        await markNotificationAsRead(notification);
      } catch (error) {
        return;
      }
      const updatedNotifications = notifications.map((notif) =>
        notif._id === notification._id ? { ...notif, leido: true } : notif,
      );
      setNotifications(updatedNotifications);
    }

    if (notification.modulo === "requirement") {
      const requirementId = notification.datosMensaje.idObjeto;
      navigate("/requirements", {
        state: {
          objectId: requirementId,
        },
      });
    } else if (
      notification.modulo === "event" ||
      notification.modulo === "b2bEvent"
    ) {
      navigate(`/events`);
    } else if (notification.modulo === "connection") {
      if (notification.tipo === "connectionRequest") {
        navigate(`/chat`, {
          state: {
            activeView: "requests",
          },
        });
      } else {
        navigate(`/chat`);
      }
    } else {
      // Unsupported notification module. No navigation required.
    }
  };

  const markNotificationAsRead = async (notification) => {
    const options = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        idUsuario: notification.idUsuario,
        SK: notification.SK,
        tipo: notification.tipo,
      }),
    };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}notification/read`,
        options,
      );

      const data = await response.json();
      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await markNotificationAsRead(notification);
      }
    } catch (error) {
      logger.error("Error marking notification as read:", {
        err: sanitizeError(error),
      });
      throw error; // Re-throw the error to handle it in the calling function
    }
  };

  return (
    <div className={styles.notificationsContainer} ref={containerRef}>
      {unreadCount > 0 ? (
        <div className={styles.notificationBellBadge}>
          <span className={styles.badge}>{unreadCount}</span>
        </div>
      ) : null}
      <IconButton
        style={{
          flex: 1,
          display: "flex",
          color: notificationMenuOpened ? "white" : "black",
          backgroundColor: notificationMenuOpened ? "#1d5ae8" : "#ffffff",
          width: "32px",
          height: "32px",
        }}
        onClick={handleNotificationMenuOpen}
      >
        <NotificationsNoneRoundedIcon />
      </IconButton>
      <div
        className={
          notificationMenuOpened
            ? styles.notificationMenu
            : styles.notificationMenuHidden
        }
      >
        <div className={styles.menuContainer}>
          <div className={styles.menuHeader}>
            <span className={styles.menuTitle}>
              {t("components.notificationsList.title")}
            </span>
            {unreadCount > 0 && (
              <button
                className={styles.markAllAsReadButton}
                onClick={() => markAllAsRead()}
              >
                {t("components.notificationsList.markAsRead")}
              </button>
            )}
          </div>
          <div className={styles.notificationList}>
            {notifications.length === 0 ? (
              <div className={styles.noNotifications}>
                {t("components.notificationsList.noNotifications")}
              </div>
            ) : (
              notifications.map((notification, index) => (
                <div
                  key={index}
                  className={`${styles.notificationItem} ${
                    notification.leido ? styles.read : ""
                  }`}
                  onClick={() => goToPage(notification)}
                >
                  <span className={styles.message}>
                    {formatNotificationMessage(notification)}
                  </span>
                  <div className={styles.notificationDetails}>
                    <span
                      className={`${styles.notificationModule} ${
                        notification.leido ? styles.read : ""
                      }`}
                    >
                      {formatNotificationModule(notification.modulo)}
                    </span>
                    <span className={styles.date}>
                      {formatTimePassed(notification.fechaDeCreacion)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationBell;
